import {
  extractErrorShape,
  logAuthEvent,
  newAuthEventId,
} from "@/lib/authLogging";

/**
 * Marker base for any auth condition that should bounce the user to
 * re-auth. Sibling subclasses narrow the reason; callers branch in any
 * order — the subclasses are mutually exclusive.
 */
export class TerminalAuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TerminalAuthError";
  }
}

/**
 * Thrown when the Auth0 SDK rejects a refresh attempt with a terminal OAuth
 * error code (the refresh token is invalid, revoked, or expired), or when
 * the session is decrypted but missing its refresh token (torso, ADR 0005).
 *
 * Distinct from {@link SessionExpiredError}: this fires server-side at the
 * moment the SDK reports a refresh failure. `SessionExpiredError` fires when
 * an upstream API call returns a `session_expired` body after the browser's
 * 401-retry refresh attempt has already failed.
 */
export class RefreshTokenError extends TerminalAuthError {
  constructor(message = "Refresh token is invalid or expired") {
    super(message);
    this.name = "RefreshTokenError";
  }
}

/** Thrown when `auth0.getSession()` returns null. */
export class NoSessionError extends TerminalAuthError {
  constructor(message = "No session cookie present") {
    super(message);
    this.name = "NoSessionError";
  }
}

const TERMINAL_REFRESH_ERROR_CODES = new Set(["invalid_grant", "access_denied"]);

// Practical cause-chain depth for real Auth0/OAuth wrappers is 2-3. The cap
// guards against pathological wrapping; if it ever fires we want a signal,
// not silent misclassification (a deep terminal cause would otherwise
// surface as refresh_failed → 500 instead of session_expired → 401).
const CAUSE_CHAIN_MAX_DEPTH = 4;

// `failed_to_refresh_token` is intentionally NOT in the terminal set — it's
// the SDK's wrapper code for *any* refresh failure (including transient
// network/timeout errors). Walking the cause chain lets us distinguish a
// truly terminal OAuth error (`invalid_grant` / `access_denied` nested
// inside the wrapper) from a transient blip.
function hasTerminalRefreshErrorCode(error: unknown, depth = 0): boolean {
  if (!error) return false;
  if (depth > CAUSE_CHAIN_MAX_DEPTH) {
    logAuthEvent({
      event: "auth.error_cause_chain_too_deep",
      auth_event_id: newAuthEventId(),
      depth,
    });
    return false;
  }

  let code: string | undefined;
  if (typeof error === "object" && error !== null && "code" in error) {
    const rawCode = (error as { code?: unknown }).code;
    if (typeof rawCode === "string") code = rawCode;
  }
  if (code && TERMINAL_REFRESH_ERROR_CODES.has(code)) return true;

  const cause =
    typeof error === "object" && error !== null && "cause" in error
      ? (error as { cause?: unknown }).cause
      : undefined;
  return hasTerminalRefreshErrorCode(cause, depth + 1);
}

export function isRefreshTokenError(error: unknown): boolean {
  if (error instanceof TerminalAuthError) return true;
  return hasTerminalRefreshErrorCode(error);
}

export function logRefreshTokenError(error: unknown): void {
  logAuthEvent({
    event: "server_auth.refresh_token_error",
    auth_event_id: newAuthEventId(),
    error: extractErrorShape(error),
  });
}

/**
 * Thrown when an upstream API call returns a `session_expired` or
 * `not_authenticated` body after a refresh attempt has already been made
 * (or could not be made). On the browser this is converted to a
 * {@link notifySessionExpired} call by the fetch helpers; on the server it
 * propagates to the caller.
 */
export class SessionExpiredError extends Error {
  constructor(
    public readonly reason:
      | "session_expired"
      | "not_authenticated" = "session_expired",
  ) {
    super(
      reason === "session_expired"
        ? "Your session has expired. Please log in again."
        : "You are not authenticated. Please log in.",
    );
    this.name = "SessionExpiredError";
  }
}
